import { Copy04Icon } from "@/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from "@/kit/ui/tooltip";
import { useClipboardAvailable } from "@/utils/hooks";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment, useSubscription } from "react-relay";
import LogoIcon from "./LogoIcon";
import { Button } from "@/kit/ui/button";
import { memo, MouseEventHandler, useState } from "react";
import { assertNever } from "@/utils/helpers";
import { DatasetVersionStatus } from "./__generated__/DatasetDOISubscription.graphql";
import { WorkspaceDOIFragment$key } from "./__generated__/WorkspaceDOIFragment.graphql";
import { WorkspaceDOISubscription } from "./__generated__/WorkspaceDOISubscription.graphql";
import { toast } from "@/utils/toast";
import { GeneratingDOI } from "./GeneratingDOI";

const Fragment = graphql`
  fragment WorkspaceDOIFragment on WorkspaceVersion {
    id
    bibtex
    doi {
      full
      short
      url
    }
  }
`;

interface WorkspaceDOIProps {
  workspaceVersion: WorkspaceDOIFragment$key;
}

export default function WorkspaceDOI({ ...props }: WorkspaceDOIProps) {
  const workspaceVersion = useFragment(Fragment, props.workspaceVersion);
  const clipboard = useClipboardAvailable();

  if (!workspaceVersion.doi) return null;
  const { full, short, url } = workspaceVersion.doi;

  const onCopy: MouseEventHandler = (evt) => {
    evt.preventDefault();

    if (workspaceVersion.bibtex) {
      navigator.clipboard.writeText(workspaceVersion.bibtex);
      toast.success(
        <FormattedMessage defaultMessage="BibTeX copied to clipboard" />,
      );
    } else {
      toast.error(
        <FormattedMessage defaultMessage="Failed to fetch BibTeX citation, please report this bug!" />,
      );
    }
  };

  return (
    <Tooltip delayDuration={0}>
      <div className="self-stretch flex gap-2">
        <a
          href={url}
          target="_blank"
          className="flex-1 inline-flex border border-grey-200 rounded-md overflow-clip"
        >
          <span className="flex items-center gap-2 py-1.5 px-3 bg-linear-to-b from-white to-[#EFEDFC]">
            <LogoIcon className="w-4 h-4" />
            <span className="font-medium text-sm text-brand-600">
              <FormattedMessage defaultMessage="DOI" />
            </span>
          </span>

          <span className="flex-1 text-center content-center font-medium text-sm text-grey-900">
            {short ?? full}
          </span>
        </a>

        {clipboard && (
          <TooltipTrigger asChild>
            <Button
              onClick={onCopy}
              size="md"
              variant="secondary"
              className="p-2"
            >
              <Copy04Icon className="h-5 w-5 text-grey-700" />
            </Button>
          </TooltipTrigger>
        )}
      </div>
      <TooltipPortal>
        <TooltipContent>
          <FormattedMessage defaultMessage="Copy BibTeX" />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

const Subscription = graphql`
  subscription WorkspaceDOISubscription($workspaceVersionId: ID!) {
    workspaceVersionUpdate(workspaceVersionId: $workspaceVersionId) {
      status
      workspaceVersion {
        ...WorkspaceDOIFragment
      }
    }
  }
`;

export interface PendingWorkspaceDOIProps {
  workspaceVersion: WorkspaceDOIFragment$key;
}

// Memo is needed to correctly pull and react to events sent by server.
const PendingWorkspaceDOI = memo(function PendingDatasetDOI(
  props: PendingWorkspaceDOIProps,
) {
  const { id } = useFragment(Fragment, props.workspaceVersion);

  const [state, setState] = useState<DatasetVersionStatus>("GENERATING_DOI");

  useSubscription<WorkspaceDOISubscription>({
    subscription: Subscription,
    variables: { workspaceVersionId: id },
    onNext(next) {
      setState(next?.workspaceVersionUpdate?.status ?? "IDLE");
    },
  });

  switch (state) {
    case "IDLE":
      return null;

    case "GENERATING_DOI":
      return <GeneratingDOI />;

    default:
      assertNever(state);
  }
});

WorkspaceDOI.Pending = PendingWorkspaceDOI;
